import React, { createContext, useContext, type ReactNode } from 'react';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import {
	type ProjectKey,
	type ProjectId,
	toProjectId,
	toProjectKey,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { usePathParam } from '@atlassian/react-resource-router';

export const useProjectKey = (): ProjectKey => {
	const [projectKey] = usePathParam('projectKey');
	if (projectKey == null) {
		throw new Error('Project key missing');
	}
	return toProjectKey(projectKey);
};

const ProjectIdContext = createContext<ProjectId | null>(null);

export const ProjectIdProvider = ({ children }: { children: ReactNode }) => {
	const { data } = useProjectContext();
	const projectId = data === null ? null : toProjectId(`${data.projectId}`);
	return (
		<ProjectIdContext.Provider value={projectId}>
			{projectId === null ? null : children}
		</ProjectIdContext.Provider>
	);
};

export const useProjectId = (): ProjectId => {
	const projectId = useContext(ProjectIdContext);
	if (projectId === null) {
		throw new Error('Project ID provider missing');
	}
	return projectId;
};

export const useProjectAdmin = (): boolean | null => {
	const { data } = useProjectContext();
	if (data === null) {
		return null;
	}
	const { isProjectAdmin } = data;
	return isProjectAdmin === undefined ? null : isProjectAdmin;
};

export const useProjectSimplified = (): boolean | null => {
	const { data } = useProjectContext();
	return data === null ? null : data.simplified;
};

type ProjectAnalyticsAttributes = {
	isSimplified: boolean;
	isProjectAdmin: boolean;
	projectId: ProjectId;
} | null;

export const useProjectAnalyticsAttributes = (): ProjectAnalyticsAttributes => {
	const projectId = useProjectId();
	const isProjectAdmin = useProjectAdmin();
	const isProjectSimplified = useProjectSimplified();

	if (projectId === null || isProjectAdmin === null || isProjectSimplified === null) {
		return null;
	}

	return {
		isSimplified: isProjectSimplified,
		isProjectAdmin,
		projectId,
	};
};
